import { supabase } from "../config/supabase";

/**
 * Service for managing CIPA members
 * NR-05 compliance: job stability, reelection rules, member types
 */

export type TipoMembro = "eleito" | "indicado";
export type RepresentacaoMembro = "empregados" | "empregador";

export interface Membro {
  id?: string;
  empresa_id: string;
  usuario_id?: string | null;
  mandato_id: string;
  nome: string;
  email?: string | null;
  cpf_hash?: string | null;
  cargo: string;
  tipo: TipoMembro;
  representacao: RepresentacaoMembro;
  matricula?: string | null;
  setor?: string | null;
  telefone?: string | null;
  data_posse: string;
  data_saida?: string | null;
  estabilidade_ate?: string | null;
  reeleito: boolean;
  numero_reeleicoes: number;
  ativo: boolean;
  observacoes?: string | null;
  created_at?: string;
  updated_at?: string;
  criado_por?: string | null;
}

export interface MembroComMandato extends Membro {
  mandato_numero?: number;
  mandato_data_inicio?: string;
  mandato_data_fim?: string;
}

/**
 * Service for CIPA member management
 */
export const membroService = {
  /**
   * Cria um novo membro da CIPA.
   * Calcula estabilidade para eleitos e valida duplicidade no mandato.
   * @param membro - Dados do membro (sem id/created_at/updated_at).
   * @returns Membro criado.
   * @throws Erro de validação ou falha na persistência.
   */
  async criar(
    membro: Omit<Membro, "id" | "created_at" | "updated_at">,
  ): Promise<Membro> {
    try {
      // Validate required fields
      if (!membro.nome) throw new Error("Nome é obrigatório");
      if (!membro.cargo) throw new Error("Cargo é obrigatório");
      if (!membro.mandato_id) throw new Error("Mandato é obrigatório");

      // Calculate estabilidade_ate for elected members (NR-05)
      if (membro.tipo === "eleito" && !membro.estabilidade_ate) {
        const dataPosse = new Date(membro.data_posse);
        const estabilidade = new Date(dataPosse);

        // Get mandate end date
        const { data: mandato } = await supabase
          .from("cp_cipa_mandato")
          .select("data_fim")
          .eq("id", membro.mandato_id)
          .single();

        if (mandato) {
          estabilidade.setTime(new Date(mandato.data_fim).getTime());
          // Add 1 year after mandate end (NR-05 job stability)
          estabilidade.setFullYear(estabilidade.getFullYear() + 1);
          membro.estabilidade_ate = estabilidade.toISOString().split("T")[0];
        }
      }

      // Check for duplicate member in same mandate
      const { data: existing } = await supabase
        .from("cp_cipa_membros")
        .select("id")
        .eq("mandato_id", membro.mandato_id)
        .eq("nome", membro.nome)
        .eq("cargo", membro.cargo);

      if (existing && existing.length > 0) {
        throw new Error("Membro já cadastrado neste mandato");
      }

      const { data, error } = await supabase
        .from("cp_cipa_membros")
        .insert([membro])
        .select()
        .single();

      if (error) {
        console.error("❌ Erro ao criar membro:", error);
        throw error;
      }

      console.log("✅ Membro criado com sucesso:", data);
      return data as Membro;
    } catch (error) {
      console.error("❌ Erro ao criar membro:", error);
      throw error;
    }
  },

  /**
   * Atualiza um membro existente.
   * @param id - ID do membro.
   * @param updates - Campos a atualizar.
   * @returns Membro atualizado.
   * @throws Erro quando a atualização falhar.
   */
  async atualizar(id: string, updates: Partial<Membro>): Promise<Membro> {
    try {
      const { data, error } = await supabase
        .from("cp_cipa_membros")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("❌ Erro ao atualizar membro:", error);
        throw error;
      }

      console.log("✅ Membro atualizado com sucesso:", data);
      return data as Membro;
    } catch (error) {
      console.error("❌ Erro ao atualizar membro:", error);
      throw error;
    }
  },

  /**
   * Obtém um membro pelo ID, incluindo dados do mandato.
   * @param id - ID do membro.
   * @returns Membro com dados de mandato ou null.
   */
  async obter(id: string): Promise<MembroComMandato | null> {
    try {
      const { data, error } = await supabase
        .from("cp_cipa_membros")
        .select(
          `
          *,
          mandato:cp_cipa_mandato!mandato_id(numero_mandato, data_inicio, data_fim)
        `,
        )
        .eq("id", id)
        .single();

      if (error) throw error;

      return {
        ...data,
        mandato_numero: data.mandato?.numero_mandato,
        mandato_data_inicio: data.mandato?.data_inicio,
        mandato_data_fim: data.mandato?.data_fim,
      } as MembroComMandato;
    } catch (error) {
      console.error("❌ Erro ao obter membro:", error);
      return null;
    }
  },

  /**
   * Lista membros de uma empresa.
   * @param empresaId - ID da empresa.
   * @returns Lista de membros com dados do mandato.
   */
  async listar(empresaId: string): Promise<MembroComMandato[]> {
    try {
      const { data, error } = await supabase
        .from("cp_cipa_membros")
        .select(
          `
          *,
          mandato:cp_cipa_mandato!mandato_id(numero_mandato, data_inicio, data_fim)
        `,
        )
        .eq("empresa_id", empresaId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("❌ Erro ao listar membros:", error);
        throw error;
      }

      return (data || []).map((m) => ({
        ...m,
        mandato_numero: m.mandato?.numero_mandato,
        mandato_data_inicio: m.mandato?.data_inicio,
        mandato_data_fim: m.mandato?.data_fim,
      })) as MembroComMandato[];
    } catch (error) {
      console.error("❌ Erro ao listar membros:", error);
      throw error;
    }
  },

  /**
   * Lista membros ativos por mandato.
   * @param mandatoId - ID do mandato.
   * @returns Lista de membros ativos.
   */
  async listarPorMandato(mandatoId: string): Promise<Membro[]> {
    try {
      const { data, error } = await supabase
        .from("cp_cipa_membros")
        .select("*")
        .eq("mandato_id", mandatoId)
        .eq("ativo", true)
        .order("nome");

      if (error) {
        console.error("❌ Erro ao listar membros do mandato:", error);
        throw error;
      }

      return (data || []) as Membro[];
    } catch (error) {
      console.error("❌ Erro ao listar membros do mandato:", error);
      throw error;
    }
  },

  /**
   * Lista membros ativos da empresa (somente mandatos ativos).
   * @param empresaId - ID da empresa.
   * @returns Lista de membros ativos com dados do mandato.
   */
  async listarAtivos(empresaId: string): Promise<MembroComMandato[]> {
    try {
      const { data, error } = await supabase
        .from("cp_cipa_membros")
        .select(
          `
          *,
          mandato:cp_cipa_mandato!mandato_id(numero_mandato, data_inicio, data_fim, status)
        `,
        )
        .eq("empresa_id", empresaId)
        .eq("ativo", true)
        .order("nome");

      if (error) {
        console.error("❌ Erro ao listar membros ativos:", error);
        throw error;
      }

      // Filter only members from active mandates
      const membrosAtivos = (data || []).filter(
        (m) => m.mandato?.status === "ativo",
      );

      return membrosAtivos.map((m) => ({
        ...m,
        mandato_numero: m.mandato?.numero_mandato,
        mandato_data_inicio: m.mandato?.data_inicio,
        mandato_data_fim: m.mandato?.data_fim,
      })) as MembroComMandato[];
    } catch (error) {
      console.error("❌ Erro ao listar membros ativos:", error);
      throw error;
    }
  },

  /**
   * Desativa um membro (marca como inativo e registra data de saída).
   * @param id - ID do membro.
   * @param motivo - Motivo da desativação (opcional).
   * @returns Membro atualizado.
   */
  async desativar(id: string, motivo?: string): Promise<Membro> {
    try {
      const updates: Partial<Membro> = {
        ativo: false,
        data_saida: new Date().toISOString().split("T")[0],
      };

      if (motivo) {
        updates.observacoes = `Desativado: ${motivo}`;
      }

      return await this.atualizar(id, updates);
    } catch (error) {
      console.error("❌ Erro ao desativar membro:", error);
      throw error;
    }
  },

  /**
   * Reativa um membro desativado.
   * @param id - ID do membro.
   * @returns Membro atualizado.
   */
  async reativar(id: string): Promise<Membro> {
    try {
      return await this.atualizar(id, {
        ativo: true,
        data_saida: null,
      });
    } catch (error) {
      console.error("❌ Erro ao reativar membro:", error);
      throw error;
    }
  },

  /**
   * Exclui um membro (somente se não houver presenças registradas).
   * @param id - ID do membro.
   * @throws Erro quando há presenças vinculadas.
   */
  async excluir(id: string): Promise<void> {
    try {
      // Check if member has attendance records
      const { count } = await supabase
        .from("cp_membros_presenca")
        .select("*", { count: "exact", head: true })
        .eq("membro_id", id);

      if (count && count > 0) {
        throw new Error(
          "Não é possível excluir membro com registros de presença",
        );
      }

      const { error } = await supabase
        .from("cp_cipa_membros")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("❌ Erro ao excluir membro:", error);
        throw error;
      }

      console.log("✅ Membro excluído com sucesso");
    } catch (error) {
      console.error("❌ Erro ao excluir membro:", error);
      throw error;
    }
  },

  /**
   * Verifica se o membro possui estabilidade vigente.
   * @param membro - Membro da CIPA.
   * @returns true se está em estabilidade.
   */
  temEstabilidade(membro: Membro): boolean {
    if (!membro.estabilidade_ate) return false;
    const estabilidade = new Date(membro.estabilidade_ate);
    const hoje = new Date();
    return estabilidade > hoje;
  },

  /**
   * Calcula dias restantes de estabilidade.
   * @param membro - Membro da CIPA.
   * @returns Dias restantes (0 se não aplicável).
   */
  diasEstabilidadeRestantes(membro: Membro): number {
    if (!membro.estabilidade_ate) return 0;
    const estabilidade = new Date(membro.estabilidade_ate);
    const hoje = new Date();
    const diffTime = estabilidade.getTime() - hoje.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  },

  /**
   * Verifica se o membro pode ser reeleito (NR-05: 1 reeleição).
   * @param membro - Membro da CIPA.
   * @returns true se pode ser reeleito.
   */
  podeSerReeleito(membro: Membro): boolean {
    return membro.tipo === "eleito" && membro.numero_reeleicoes < 1;
  },

  /**
   * Obtém estatísticas de membros por mandato.
   * @param mandatoId - ID do mandato.
   * @returns Totais por tipo, representação e estabilidade.
   */
  async obterEstatisticas(mandatoId: string): Promise<{
    total: number;
    eleitos: number;
    indicados: number;
    empregados: number;
    empregador: number;
    comEstabilidade: number;
  }> {
    try {
      const membros = await this.listarPorMandato(mandatoId);

      return {
        total: membros.length,
        eleitos: membros.filter((m) => m.tipo === "eleito").length,
        indicados: membros.filter((m) => m.tipo === "indicado").length,
        empregados: membros.filter((m) => m.representacao === "empregados")
          .length,
        empregador: membros.filter((m) => m.representacao === "empregador")
          .length,
        comEstabilidade: membros.filter((m) => this.temEstabilidade(m)).length,
      };
    } catch (error) {
      console.error("❌ Erro ao obter estatísticas:", error);
      throw error;
    }
  },
};
